import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTenant } from '../../contexts/TenantContext';

import LoginPage from './LoginPage';
import PremiumLoginPage from './PremiumLoginPage';
import ForgotPasswordPage from './ForgotPasswordPage';
import PremiumForgotPasswordPage from './PremiumForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import PremiumResetPasswordPage from './PremiumResetPasswordPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useTenantConfig() {
  const [searchParams] = useSearchParams();
  const { tenantConfig: ctxConfig, tenantSlug: ctxSlug, isTenantPortal, loading: ctxLoading } = useTenant();

  const querySlug = searchParams.get('tenant') || '';
  const [queryConfig, setQueryConfig] = useState(null);
  const [queryLoading, setQueryLoading] = useState(!!querySlug && !isTenantPortal);

  useEffect(() => {
    if (isTenantPortal || !querySlug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/tenants/by-slug/${querySlug}/login-config`);
        if (!cancelled) setQueryConfig(res.data);
      } catch {
        if (!cancelled) setQueryConfig(null);
      } finally {
        if (!cancelled) setQueryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [querySlug, isTenantPortal]);

  // Path-based tenant takes priority, then query param
  if (isTenantPortal) {
    return { tenantConfig: ctxConfig, loading: ctxLoading };
  }
  return { tenantConfig: queryConfig, loading: queryLoading };
}

export function LoginRouter() {
  const { tenantConfig, loading } = useTenantConfig();

  if (loading) {
    return (
      <div className="prem-auth-page" data-testid="login-router-loading">
        <div className="prem-auth-loading-spinner" />
      </div>
    );
  }

  if (tenantConfig?.login_experience === 'premium_login') {
    return <PremiumLoginPage tenantConfig={tenantConfig} />;
  }

  return <LoginPage />;
}

export function ForgotPasswordRouter() {
  const { tenantConfig, loading } = useTenantConfig();

  if (loading) {
    return (
      <div className="prem-auth-page" data-testid="forgot-router-loading">
        <div className="prem-auth-loading-spinner" />
      </div>
    );
  }

  if (tenantConfig?.login_experience === 'premium_login') {
    return <PremiumForgotPasswordPage tenantConfig={tenantConfig} />;
  }

  return <ForgotPasswordPage />;
}

export function ResetPasswordRouter() {
  const { tenantConfig, loading } = useTenantConfig();

  if (loading) {
    return (
      <div className="prem-auth-page" data-testid="reset-router-loading">
        <div className="prem-auth-loading-spinner" />
      </div>
    );
  }

  if (tenantConfig?.login_experience === 'premium_login') {
    return <PremiumResetPasswordPage tenantConfig={tenantConfig} />;
  }

  return <ResetPasswordPage />;
}
